import clsx from "clsx";
import dayjs from "dayjs";
import sample from "lodash/sample";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import Image from 'next/image';
import { useEffect, useState } from "react";
import { Seomatic } from "react-seomatic";

import BlogLayoutNewsletterForm from "@components/BlogLayoutNewsletterForm";
import ColumnLayout from "@components/ColumnLayout";
import ContentSections from "@components/ContentSections";
import EntryCarousel from "@components/EntryCarousel";
import EntryThumbnail from "@components/EntryThumbnail";
import Heading from "@components/Heading";
import LegacyContent from "@components/LegacyContent";
import PageSections from "@components/PageSections";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import { PreviewContent } from "@interfaces/PreviewContent.interface";
import { getEntryCategoryArray } from "@lib/getEntryCategoryArray";
import getImageBlurOptions from "@lib/getImageBlurOptions";
import { returnStaticProps } from "@lib/returnStaticProps";
import craft from "@pages/api/craft";
import QueryPost from "@queries/blog/QueryPost.graphql";
import QuerySlugs from "@queries/QuerySlugs.graphql";

import Authors from "@components/Authors";
import DotBorder from "@components/DotBorder";
import Share from "@components/Share";
import SidebarCallToAction from "@components/SidebarCallToAction";
import Link from "next/link";
import Script from "next/script";

export const getStaticPaths: GetStaticPaths = async (context) => {
	// Fetch every possible path
	const response = await craft(QuerySlugs, { section: "blog", limit: null });

	const paths = [];

	response.entries.map((post) => {
		console.log(`Generating /blog/${post.slug}`);

		paths.push({
			params: { slug: post.slug },
		});
	});

	return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({
	params,
	previewData: nextPreviewData,
	preview,
}) => {
	// For Craft preview capability
	const previewData: PreviewContent = nextPreviewData as PreviewContent;

	// Construct uri from the slug array
	const { slug } = params;

	const hideHeaderBuffer = true;

	// What is the uri
	const uri = `/blog/${slug}`;

	// Fetch the page's data
	const response = await craft(
		QueryPost,
		{ slug },
		preview ? previewData.token : null
	);

	const {
		pageEntry,
		post,
		posts,
		primaryNavigation,
		primaryCallToActions,
		globalSet,
	} = response;

	// Return props if entry exists, otherwise check Retour for redirects or 404
	const returnResponse = await returnStaticProps(
		{
			navigation: {
				primaryNavigation,
				primaryCallToActions,
			},
			pageEntry,
			globalSet,
			post,
			posts,
			hideHeaderBuffer,
		},
		!post,
		uri
	);

	return returnResponse;
};

interface IProps {
	pageEntry: any;
	post: any;
	posts: any;
}

export default function Page({ pageEntry, post, posts }: IProps): JSX.Element {
	useSetHeaderTheme(pageEntry.headerTheme);

	// Select just one call to action at random

	const parsedDate = dayjs(post.postDate);
	let formData;
	const sidebarCallToAction = sample(pageEntry.callToActions);
	const relatedPosts = posts.slice(0, 4);

	const handleImageHeight = (newImageHeight) => {
		setThumbnailImageHeight((oldHeight) =>
			newImageHeight > 0 ? newImageHeight : oldHeight
		);
	};

	const [thumbnailImageHeight, setThumbnailImageHeight] = useState(100);

	let faqs = [];
	let faqSchema = {};

	if (post.faqSchema?.length) {
		post.faqSchema.map((item) => {
			faqs.push({
				"@type": "Question",
				name: item.question,
				acceptedAnswer: {
					"@type": "Answer",
					text: item.answer,
				},
			});
		});

		faqSchema = {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: faqs,
		};
	}

	return (
		<>
			<Seomatic Head={Head} {...post.seomatic} />

			{post.faqSchema?.length > 0 && (
				<Script
					type="application/ld+json"
					id="faqSchema"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
				/>
			)}

			<div className={clsx("relative bg-white-01")}>
				<div className={clsx("PostContent relative py-15")}>
					<ColumnLayout>
						<ColumnLayout.MainColumn>
							<Link
								href="/resources"
								className="Attack__return font-medium border-b-[3px] border-purple-01 pb-0 mb-7.5 inline-block"
							>
								Back to All Research
							</Link>

							<h1 className={clsx("text-3xl font-medium mb-7.5")}>
								{post.title}
							</h1>
							{post.summary && post.showSummary && (
								<div className="Post__summary text-xl mb-7.5">
									{post.summary}
								</div>
							)}
							{!!post.postAuthors && (
								<div className={clsx("EntryHeader__authors mb-10")}>
									<Authors authors={post.postAuthors} />
								</div>
							)}
							{!!post.postDate && (
								<div
									className={clsx(
										"PostContent--date",
										"mt-7.5 text-xs mb-15 text-gray-01"
									)}
								>
									{parsedDate.format("MMMM D, YYYY")}
								</div>
							)}
							<hr className={clsx("mb-15")} />
							{!!post.contentSections && (
								<ContentSections sections={post.contentSections} />
							)}
							{!!post.legacyBlogContent && (
								<LegacyContent content={post.legacyBlogContent} />
							)}
						</ColumnLayout.MainColumn>
						<ColumnLayout.SidebarColumn className={clsx("lg:pb-15")}>
							{!!post.image[0] && !!post.image[0].url && (
								<div className="EntryThumbnail__container mb-15">
									<Image
										src={post.image[0].url}
										height={post.image[0].height}
										width={post.image[0].width}
										alt={post.image[0].title}
										layout="responsive"
										className={clsx("EntryThumbnail__post.image[0]")}
										draggable={false}
										{...getImageBlurOptions(post.image[0], post.imageBlur[0])}
									/>
								</div>
							)}
							{!!sidebarCallToAction && (
								<SidebarCallToAction
									heading={sidebarCallToAction.heading}
									image={sidebarCallToAction.image[0]}
									imageBlur={sidebarCallToAction.imageBlur[0]}
									target={sidebarCallToAction.target}
								/>
							)}
							<div className="EntryForm__container mb-7.5">
								<BlogLayoutNewsletterForm
									formHeading={pageEntry.formHeading}
									formDescription={pageEntry.formDescription}
									form={pageEntry?.form?.[0]}
									formData={formData}
									successMessage={pageEntry.successMessage}
								/>
							</div>
							<div
								className={clsx(
									"EntryHeader__share bg-black-01 text-white-01 py-15 px-7.5",
									"flex"
								)}
							>
								<Share url={post.url} title={post.heading} />
							</div>
						</ColumnLayout.SidebarColumn>
					</ColumnLayout>
					<DotBorder bottomOnly />
				</div>

				<div className={clsx("RelatedContent bg-grid relative py-22")}>
					<div
						className={clsx(
							"max-w-screen-xl mx-auto relative",
							"bg-white-01 text-center py-5 mb-15 box-sizing"
						)}
					>
						<Heading size="h2">Related Posts</Heading>
					</div>

					<EntryCarousel
						isFlush={false}
						thumbnailImageHeight={thumbnailImageHeight}
						thumbnailAspectRatio={"square"}
					>
						{posts.map((entry) => {
							const image = !!entry.image ? entry.image[0] : null;
							const imageBlur = !!entry.imageBlur ? entry.imageBlur[0] : null;
							return (
								<EntryThumbnail
									key={entry.id}
									image={image}
									imageBlur={imageBlur}
									heading={entry.title}
									summary={entry.summary}
									target={{ element: entry, type: "entry" }}
									categories={getEntryCategoryArray(entry)}
									onImageHeight={handleImageHeight}
									aspectRatio={"square"}
								/>
							);
						})}
					</EntryCarousel>
				</div>
			</div>
		</>
	);
}
